/**
 * Glue layer between game state, the three football.riv artboards, the minimal
 * DOM controls, and the multiplayer hub. Mirrors the imperative orchestration
 * in `GameBloc` of
 *   packages/amgfootball_v2/lib/presentation/bloc/game_bloc.dart
 *
 * Football is driven by FIXED-DURATION timers (not Rive completion events).
 * Single-player result cycle ≈ 13 s; see runResultSequence().
 *
 * Input flow:
 *   Rive evtBetClick {betID}    → tryPlaceBet(betTypeFromId(betID))
 *   Rive evtClickChips {chipsAmt}→ selectChip(chipsAmt - 1)   (chipsAmt is 1-based!)
 *   DOM  PLAY button            → confirm()   (single-player only)
 *   DOM  CLEAR button           → clearAll()
 */
import type { BetType, GameResult } from '../types/game';
import { betTypeFromId, ballShootFor } from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_ODDS,
  DEFAULT_PASSWORD,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
  MP_BET_COUNTDOWN_OFFSET_S,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// showModeSelectionDialog disabled — mode now comes from the launch URL
// (g param / path). Kept importable from '../ui/modeSelectionDialog' if re-enabled.
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import { parseResultString, parseGameInitialInfo } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  params: UrlParams;
  onInteractive?: () => void;
}

type Envelope = Record<string, unknown> | null;

const SIDES: BetType[] = ['left', 'center', 'right'];

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid = '';

  private resultSequenceRunning = false;
  private sequenceTimers: number[] = [];

  // Balance settlement is deferred to the WIN/LOSE reveal (SP: the t=7s
  // timer in runResultSequence; MP: the panel shown in handleResult) so the
  // displayed (available) balance stays "bet deducted" through the card/ball
  // reveal and only reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Bumped each round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile.
  private roundToken = 0;
  // Guards applyResultSettlement to once per round.
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;

  // Multiplayer timers / dedup.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.setControlsEnabled(false, false);
    this.d.rive.setIsMobile('ontouchstart' in window);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.rive.setUsernameText(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode selection dialog disabled — the mode is now derived from the
      // launch URL the lobby routes to (single `/?g=SSC1` vs multi
      // `/goalshootinghub?g=SSCM`; see urlParams.resolveGameMode) instead of
      // prompting the player at launch. Original dialog code kept below
      // (commented) for reference.
      //
      // this.d.state.setPhase('modeSelection');
      // this.d.hud.setStatus('Select mode');
      // const mode: GameMode = await showModeSelectionDialog();
      // debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      if (mode === 'multi') {
        await this.startMultiplayer();
      } else {
        this.startFirstBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap (login → openGame → balance → getInfo)
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });
      debugLog('[Orchestrator] openGame OK, currentGameUrl=', session.currentGameUrl);

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      if (balance > 0) this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
          odds: [...DEFAULT_ODDS],
          initialCards: [0, 0],
        });
      }
    }
  }

  private applyServerConfig(cfg: {
    chips: number[];
    minBet: number;
    maxBet: number;
    odds: string[];
    initialCards: number[];
  }): void {
    const cur = this.d.state.config;
    this.d.state.setConfig({
      minBet: cfg.minBet > 0 ? cfg.minBet : cur.minBet,
      maxBet: cfg.maxBet > 0 ? cfg.maxBet : cur.maxBet,
      chipValues: cfg.chips.length > 0 ? cfg.chips : [...cur.chipValues],
      odds: cfg.odds.length > 0 ? cfg.odds : [...cur.odds],
      initialCards: cfg.initialCards.length > 0 ? cfg.initialCards : [...cur.initialCards],
    });
  }

  // =================================================================
  // Rive input events
  // =================================================================

  private bindRiveEvents(): void {
    // Goal-area click → place a bet. betID is the numeric bet item id.
    this.d.rive.on('evtBetClick', (payload) => {
      const id = (payload.betID ?? payload.betId ?? payload.id) as number | undefined;
      if (typeof id !== 'number') return;
      const bet = betTypeFromId(Math.round(id));
      if (bet) this.tryPlaceBet(bet);
    });

    // Chip picker → select chip. chipsAmt is the 1-BASED chip index.
    this.d.rive.on('evtClickChips', (payload) => {
      const raw = (payload.chipsAmt ?? payload.chipIndex ?? payload.index) as
        | number
        | undefined;
      if (typeof raw !== 'number') return;
      this.d.state.selectChip(Math.round(raw) - 1);
    });

    // Declared in the .riv but the bloc ignores these — log for discovery.
    this.d.rive.on('placeBetClick', (p) => debugLog('[Rive] placeBetClick (ignored)', p));
    this.d.rive.on('UIButtonClick', (p) => debugLog('[Rive] UIButtonClick (ignored)', p));
  }

  // =================================================================
  // Player actions
  // =================================================================

  tryPlaceBet(bet: BetType): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chip = s.selectedChipValue;
    const ok = s.placeBet(bet, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', bet, chip);
      const maxBet = s.config.maxBet;
      if (maxBet > 0 && s.bets.total + chip > maxBet) {
        this.d.hud.setStatus(`Maximum bet is ${formatCurrency(maxBet)}`);
      }
      return;
    }
    // The available balance drops at once via onStateChange (req: balance
    // reflects the bet immediately). Bets are NOT sent to the backend here —
    // single-player submits on Play (confirm) and multiplayer submits when
    // the bet countdown ends (submitMultiplayerBets) — so they stay locally
    // clearable until then (req: player can still clear the bets).
  }

  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
  }

  /** Single-player confirm → REST /bet → 13 s result sequence. */
  async confirm(): Promise<void> {
    const s = this.d.state;
    if (s.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer');
      return;
    }
    if (s.phase !== 'betting') return;

    const bets = s.bets.toBetLines();
    if (bets.length === 0) return;
    if (s.bets.total < s.config.minBet) {
      this.d.hud.setStatus(`Minimum bet is ${formatCurrency(s.config.minBet)}`);
      return;
    }

    // No-more-bet FIRST (matches game_bloc.dart _onConfirmBets ordering).
    this.d.rive.setStartEndBet(2);
    this.d.state.setPhase('bettingClosed');
    this.d.hud.setStatus('No more bets');

    const totalBet = s.bets.total;
    const betDetails = formatBetDetails(s.bets);
    const balanceBefore = s.balance;

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({ sessionId: sid, bets, gid: this.gid || sid });
      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;
      const nextCards = parseGameInitialInfo(result.gameInitialInfo);

      this.d.state.applyRoundResult({
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        leftCard: parsed.leftCard,
        rightCard: parsed.rightCard,
        centerCard: parsed.centerCard,
        winningBetId: parsed.winningBetId,
        result: parsed.winningBet,
        totalWinLose: result.totalWinLose,
        totalBet,
        nextRoundCards: nextCards,
      });

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: formatGameResult(parsed.winningBet),
        roundId: this.gid,
      });

      // Defer the balance update to the WIN/LOSE reveal (the t=7s timer in
      // runResultSequence) so the displayed balance stays "bet deducted"
      // through the card/ball reveal and only reflects winnings when the
      // result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      this.runResultSequence();
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      this.d.rive.setStartEndBet(1);
      this.d.state.setPhase('betting');
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.hud.setStatus('ERROR: ' + msg);
      }
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * Sends ONE PlaceBet per INDIVIDUAL chip placed (via toChipLines), NOT one
   * per goal carrying the summed total. The hub validates each PlaceBet amount
   * against the configured chip denominations and rejects anything else with
   * "Invalid Chip Amount" — so a summed total like 300 (3×100) is refused even
   * though the server would otherwise add the chips up. Replaying the
   * individual chips mirrors the original per-click submission while keeping
   * our deferred/clearable batching. The single-player REST endpoint differs —
   * it DOES accept a per-goal total, so confirm() still uses toBetLines().
   * Idempotent: the didSubmitMpBets guard prevents a double-submit when both
   * the countdown tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    // Per-chip, NOT per-goal total — see the doc comment above.
    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Single-player betting / result sequences
  // =================================================================

  /**
   * First betting round after mode selection (mirrors _onStartBettingPhase):
   *   ballShoot(1) → +1s cardAnim(1) → +2s cardAnim(2) → +3s startEndBet(1).
   */
  private startFirstBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.resultSequenceRunning = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.didApplyResultSettlement = false;
    this.d.state.resetForNewRound();
    this.d.state.setPhase('betting');

    const cfg = this.d.state.config;
    this.d.rive.setLeftCard(cfg.initialCards[0] ?? 0);
    this.d.rive.setRightCard(cfg.initialCards[1] ?? 0);
    this.d.rive.setRoundText(String(this.d.state.roundNumber));
    this.d.rive.updateHistory(this.d.state.history as ReadonlyArray<0 | 1 | 2 | 3>);

    this.d.rive.setBallShoot(1);
    this.scheduleTimer(() => this.d.rive.setCardAnim(1), 1000);
    this.scheduleTimer(() => this.d.rive.setCardAnim(2), 2000);
    this.scheduleTimer(() => this.d.rive.setStartEndBet(1), 3000);

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the win/lose timer and again from the round-teardown safety
   * nets. If the server balance isn't known (an MP round whose Result never
   * arrived), just clears the bets; handleResult's late-Result path applies
   * the balance the moment it arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    this.didSettle = true;
    if (this.pendingSettleBalance != null) {
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      this.d.state.clearBets();
    }
  }

  /**
   * Single-player result animation. Exact timeline from game_bloc.dart
   * _onStartResultSequence (t = 2/3/4/7/10/11/12/13 s).
   */
  private runResultSequence(): void {
    if (this.resultSequenceRunning) return;
    this.resultSequenceRunning = true;
    this.cancelTimers();

    const round = this.d.state.currentRound;
    if (!round) {
      this.resultSequenceRunning = false;
      return;
    }
    const winLose = round.totalWinLose;

    this.d.state.setPhase('result');
    this.d.rive.setCenterCard(round.centerCard); // t=0

    this.scheduleTimer(() => this.d.rive.setCardAnim(3), 2000);
    this.scheduleTimer(() => this.d.rive.setCardAnim(4), 3000);
    this.scheduleTimer(() => {
      this.d.rive.setCardAnim(0);
      this.d.rive.setBallShoot(ballShootFor(round.result));
    }, 4000);

    this.scheduleTimer(() => {
      // Reflect the post-round balance now that the result is shown (req 2).
      // settle() clears the resolved bets in the same commit so the available
      // balance lands exactly on the server's settled value — no flash.
      this.applySettlement();
      if (winLose > 0) {
        this.d.rive.setWinLoseResult(1);
        this.d.rive.setWinAmountText(money2(winLose));
        this.d.hud.setStatus(`YOU WIN ${formatCurrency(winLose)}`);
      } else {
        this.d.rive.setWinLoseResult(2);
        this.d.hud.setStatus(winLose < 0 ? `YOU LOSE ${formatCurrency(-winLose)}` : 'No win');
      }
    }, 7000);

    // t=10s — visual cleanup only; the wallet/bets were already settled
    // atomically at the t=7s WIN/LOSE reveal.
    this.scheduleTimer(() => {
      this.d.rive.setWinLoseResult(0);
      this.d.rive.setWinAmountText('');
      this.d.rive.setBallShoot(0);
      for (const side of SIDES) this.d.rive.setAreaBetText(side, '0');
      this.d.rive.setTotalBetText('0.00');
      this.d.rive.setLeftCard(round.nextRoundCards[0] ?? 0);
      this.d.rive.setRightCard(round.nextRoundCards[1] ?? 0);
    }, 10000);

    this.scheduleTimer(() => this.d.rive.setCardAnim(1), 11000);
    this.scheduleTimer(() => this.d.rive.setCardAnim(2), 12000);
    this.scheduleTimer(() => {
      // Safety net: ensure the balance was settled even if the t=7s timer
      // was skipped. This block is also the new-round reset for rounds 2+,
      // so clear the settlement flags for the next round.
      this.applySettlement();
      this.roundToken++;
      this.pendingSettleBalance = null;
      this.didSettle = false;
      this.didApplyResultSettlement = false;
      this.d.rive.setBallShoot(1);
      this.d.rive.setStartEndBet(1);
      this.d.state.pushHistory(round.result);
      this.d.state.incrementRound();
      this.d.rive.setRoundText(String(this.d.state.roundNumber));
      this.resultSequenceRunning = false;
      this.d.state.resetForNewRound();
      this.d.state.setPhase('betting');
      this.d.hud.setStatus('Place your bets');
    }, 13000);
  }

  // =================================================================
  // State → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    const balanceOrBetsChanged =
      next.balance !== prev.balance || next.bets !== prev.bets;

    if (balanceOrBetsChanged) {
      const avail = next.balance - next.bets.total;
      this.d.hud.setBalance(avail);
      this.d.rive.setBalanceText(money2(avail));
      for (const side of SIDES) {
        this.d.rive.setAreaBetText(side, int0(next.bets.amountFor(side)));
      }
      this.d.rive.setTotalBetText(money2(next.bets.total));
    }

    if (
      next.bets !== prev.bets ||
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      const canBet = next.phase === 'betting' && (!next.isMultiplayer || next.multiplayerCanBet);
      this.d.controls.setControlsEnabled(canBet, !next.bets.isEmpty);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.rive.setChipSelect(next.selectedChipIndex + 1);
    }

    if (next.config !== prev.config) {
      const cfg = next.config;
      cfg.chipValues.forEach((v, i) => this.d.rive.setChipText(i + 1, String(v)));
      this.d.rive.setOddsText('left', cfg.odds[0] ?? '');
      this.d.rive.setOddsText('center', cfg.odds[1] ?? '');
      this.d.rive.setOddsText('right', cfg.odds[2] ?? '');
    }

    if (next.history !== prev.history) {
      this.d.rive.updateHistory(next.history as ReadonlyArray<0 | 1 | 2 | 3>);
    }

    if (next.isMultiplayer !== prev.isMultiplayer) {
      this.d.controls.setVisible(!next.isMultiplayer);
    }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  // =================================================================
  // Multiplayer
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('multiplayerSyncing');
    this.d.controls.setVisible(false);
    this.lastMpEventIndex = null;
    this.d.hud.setStatus('Connecting hub…');

    try {
      // Best-effort config refresh before joining (game_bloc.dart step 2).
      try {
        const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
        const info = await this.d.api.getInfo({ sessionId });
        this.applyServerConfig(info);
      } catch (e) {
        warn('[Orchestrator] MP pre-join getInfo failed (continuing):', e);
      }

      await this.d.hub.connect();

      // JoinGame's sessionId is the URL-DECODED bearer token (game_bloc.dart).
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      debugLog('[Orchestrator] joining hub, gameKey=', GAME_KEY_MULTI);
      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    if (event.name === '_connection') {
      this.handleConnectionState(event.payload as Record<string, unknown>);
      return;
    }

    // GetMessages is a replay ARRAY — expand & route each message before the
    // single-envelope dedup logic (otherwise we'd dedup on the array's last item).
    if (event.name === 'GetMessages') {
      this.handleGetMessages(event.payload);
      return;
    }

    const env = this.normalizeEnvelope(event.payload);

    // Index-based dedup (game_bloc.dart:914-922). Drop Index <= last.
    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }

    // Flag == 'error' is the failure signal (NOT a ['Fail', env] tuple).
    const flag = env?.Flag != null ? String(env.Flag).toLowerCase() : '';
    if (flag === 'error') {
      const content = String(env?.Content ?? '');
      if (event.name === 'PlaceBet') {
        this.d.state.clearBets();
        this.d.hud.setStatus(content || 'Bet rejected');
      } else {
        this.d.hud.setStatus(content || 'Server error');
      }
      return;
    }

    switch (event.name) {
      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;
      case 'StartGame':
        this.handleStartGame(env);
        break;
      case 'StartBet':
        this.handleStartBet(env);
        break;
      case 'EndBet':
        this.handleEndBet(env);
        break;
      case 'StartPlay':
        this.handleStartPlay(env);
        break;
      case 'Result':
        this.handleResult(env);
        break;
      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;
      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
    // NOTE: balance is applied ONLY at authoritative points (PlayerJoined +
    // Result settlement). We do NOT apply per-bet PlaceBet-ack balances — they
    // are already-deducted values that would double-count against our
    // available-balance display (wallet − pendingBets).
  }

  private handleConnectionState(payload: Record<string, unknown>): void {
    const st = String(payload?.state ?? '');
    debugLog('[Hub] connection state', st);
    if (st === 'reconnecting') {
      this.d.state.setPhase('multiplayerSyncing');
      this.d.hud.setStatus('Reconnecting…');
    } else if (st === 'closed') {
      this.d.hud.setStatus('Connection closed');
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
    }
  }

  private handlePlayerJoined(env: Envelope): void {
    const data = (env?.Data && typeof env.Data === 'object' ? env.Data : {}) as Record<
      string,
      unknown
    >;
    const game = (data.Game ?? data.game) as Record<string, unknown> | undefined;

    const chips = parseChipsField(game?.Chips ?? game?.chips ?? data.Chips ?? data.chips);
    if (chips.length > 0) {
      this.d.state.setConfig({ ...this.d.state.config, chipValues: chips });
      this.d.state.setPhase('betting');
    }
    const name = data.Username ?? data.username;
    if (typeof name === 'string' && name) this.d.rive.setUsernameText(name);
    const round = game?.Round ?? game?.round;
    if (typeof round === 'number') this.d.rive.setRoundText(String(round));

    // Initial wallet balance (authoritative).
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const bal = extractBalanceForUser(env?.Data, userName);
    if (bal != null) this.d.state.setBalance(bal);
  }

  private handleStartGame(env: Envelope): void {
    this.cancelRestartCountdown();
    const content = String(env?.Content ?? '');

    if (content.startsWith('InitialData:')) {
      // "InitialData: leftCard,rightCard,L%,C%,R%,leftOdds,centerOdds,rightOdds"
      const tokens = content.slice('InitialData:'.length).split(',').map((t) => t.trim());
      if (tokens.length >= 8) {
        const leftCard = parseInt(tokens[0], 10) || 0;
        const rightCard = parseInt(tokens[1], 10) || 0;
        const odds = [tokens[5], tokens[6], tokens[7]];
        this.d.rive.setLeftCard(leftCard);
        this.d.rive.setRightCard(rightCard);
        this.d.state.setConfig({
          ...this.d.state.config,
          odds,
          initialCards: [leftCard, rightCard],
        });
        this.d.rive.setBallShoot(1);
        this.scheduleTimer(() => this.d.rive.setCardAnim(1), 500);
        this.scheduleTimer(() => this.d.rive.setCardAnim(2), 1000);
      }
      return; // InitialData does not reset round state
    }

    // Reset branch — new round.
    this.cancelBetCountdown();
    this.cancelTimers();
    this.roundToken++;
    this.resultSequenceRunning = false;
    // Safety net: ensure the previous round's balance was settled even if
    // its Result/WIN-LOSE reveal was cut short, then clear the settlement +
    // submission flags for the new round.
    this.applySettlement();
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.resetForNewRound();
    this.d.state.setPhase('betting');
    for (const side of SIDES) this.d.rive.setAreaBetText(side, '0');
    this.d.rive.setTotalBetText('0.00');
    this.d.rive.setWinLoseResult(0);
    this.d.rive.setWinAmountText('');
  }

  private handleStartBet(env: Envelope): void {
    this.cancelRestartCountdown();
    this.cancelBetCountdown();
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    const adjusted = Math.max(0, seconds - MP_BET_COUNTDOWN_OFFSET_S);
    // Fresh bet window — allow a new batched submission this round and
    // forget the previous round's settlement flags.
    this.roundToken++;
    this.didSubmitMpBets = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.didApplyResultSettlement = false;
    this.d.state.resetForNewRound();
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    this.d.rive.setStartEndBet(1);
    for (const side of SIDES) this.d.rive.setAreaBetText(side, '0');
    this.d.rive.setTotalBetText('0.00');
    this.d.hud.setStatus('Place your bets');
    if (adjusted > 0) this.startBetCountdown(adjusted);
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.rive.setStartEndBet(2);
    this.d.hud.setStatus('No more bets');
  }

  private handleStartPlay(env: Envelope): void {
    this.d.state.setMultiplayerCanBet(false);
    const data = (env?.Data && typeof env.Data === 'object' ? env.Data : {}) as Record<
      string,
      unknown
    >;
    const raw = String(env?.Result ?? env?.result ?? data.Result ?? data.result ?? '');
    if (!raw) {
      this.d.state.setPhase('revealing');
      return;
    }
    let parsed;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      this.d.state.setPhase('revealing');
      return;
    }
    this.d.state.setMultiplayerResultString(raw);
    this.d.state.applyRoundResult({
      roundId: '',
      nextRoundId: '',
      leftCard: parsed.leftCard,
      rightCard: parsed.rightCard,
      centerCard: parsed.centerCard,
      winningBetId: parsed.winningBetId,
      result: parsed.winningBet,
      totalWinLose: 0,
      totalBet: this.d.state.bets.total,
      nextRoundCards: [0, 0],
    });
    this.d.state.setPhase('revealing');
    this.runMultiplayerResultAnimation(parsed.centerCard, parsed.winningBet);
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    if (state === 'restart') {
      this.lastMpEventIndex = null;
      this.d.state.setMultiplayerCanBet(false);
      this.d.state.setMultiplayerGameState('restart');
      this.d.state.setPhase('newRound');
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) this.startRestartCountdown(seconds);
      return;
    }

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-user figure on a loss (WinLost is now 0 even on a
    // loss) and the Result event no longer carries a per-user Balance, so the
    // wallet can't be derived locally — we read it from the server
    // (/action/balance). Async; settleFromResult drives the WIN/LOSE panel.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. NOTE: /action/getinfo carries config only (no
   *      balance), so we must use /action/balance — the same endpoint the startup
   *      balance fetch uses.
   *
   * Async because of the balance round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(winLose, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: settle the wallet, drive the
   * YOU WIN / YOU LOSE panel, and log the round. `balance` is the authoritative
   * post-round wallet (from the Result event or /action/balance); null only when
   * both sources failed, in which case we fall back to the legacy local
   * computation (`balanceBefore − totalBet + winLose`).
   */
  private applyResultSettlement(winLose: number, balance: number | null): void {
    // Once per round — a retransmitted settlement Result must not re-run this.
    // A second pass would read the already-settled wallet as `balanceBefore`,
    // corrupt the net P&L, and double-log.
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    const totalBet = this.d.state.bets.total;
    const betDetails = formatBetDetails(this.d.state.bets);
    const balanceBefore = this.d.state.balance;

    // Result `Data` rows carry WinLost, NOT a per-user Balance, so the wallet
    // must usually be derived arithmetically. Mirrors Flutter's
    // `newBalance = player.balance + totalWinLose` — but Flutter deducts the
    // stake at bet time and we don't, and WinLost is a GROSS return (stake +
    // profit on win, 0 on loss), so we apply `balanceBefore − totalBet + winLose`.
    const settledBalance =
      balance != null ? balance : balanceBefore - totalBet + winLose;

    this.d.state.setMultiplayerWinLose(winLose);

    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({ ...cur, totalWinLose: winLose });
      this.d.state.pushHistory(cur.result);
      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: winLose,
        balanceBefore,
        balanceAfter: settledBalance,
        gameResult: formatGameResult(cur.result),
        roundId: cur.roundId || null,
      });
    }

    // The WIN/LOSE panel shows synchronously below — settle right here so the
    // displayed available balance lands exactly on the server's post-round
    // value the moment the result is shown (req 2). settle() updates the
    // wallet AND clears the resolved bets in one commit (no flash). If
    // settlement already ran (a late Result after the StartGame safety net
    // already cleared the bets), write the balance directly so it isn't lost.
    this.pendingSettleBalance = settledBalance;
    if (this.didSettle) {
      this.d.state.setBalance(settledBalance);
    } else {
      this.applySettlement();
    }

    if (winLose > 0) {
      this.d.rive.setWinLoseResult(1);
      this.d.rive.setWinAmountText(money2(winLose));
      this.d.hud.setStatus(`YOU WIN ${formatCurrency(winLose)}`);
    } else {
      this.d.rive.setWinLoseResult(2);
      this.d.hud.setStatus(winLose < 0 ? `YOU LOSE ${formatCurrency(-winLose)}` : 'Round over');
    }
    this.d.state.setPhase('result');

    // t=3s reset (game_bloc.dart _handleResult reset timer) — visual cleanup
    // only; the wallet/bets were already settled atomically above when the
    // WIN/LOSE panel appeared.
    this.scheduleTimer(() => {
      this.d.rive.setWinLoseResult(0);
      this.d.rive.setWinAmountText('');
      this.d.rive.setBallShoot(0);
      for (const side of SIDES) this.d.rive.setAreaBetText(side, '0');
      this.d.rive.setTotalBetText('0.00');
    }, 3000);
  }

  private handlePlaceBetAck(_env: Envelope): void {
    // Server confirmed (part of) our batched bet submission. We intentionally
    // do NOT apply the echoed (already-deducted) balance here — the
    // available-balance display is computed locally as wallet − pendingBets,
    // so applying the ack's per-bet balance on top would double-count the
    // deduction. The Result event reconciles the wallet at settlement.
  }

  /**
   * Expand a GetMessages replay array and route each message back through the
   * per-event handlers. Index dedup prevents double-processing vs live events.
   */
  private handleGetMessages(payload: unknown): void {
    const items = Array.isArray(payload) ? payload : payload != null ? [payload] : [];
    const expanded: { name: string; env: Envelope }[] = [];
    for (const item of items) {
      const routed = this.routeEmbedded(item);
      if (routed) expanded.push(routed);
    }
    // Sort by Index asc when every message has one.
    if (expanded.length > 0 && expanded.every((m) => typeof m.env?.Index === 'number')) {
      expanded.sort((a, b) => (a.env!.Index as number) - (b.env!.Index as number));
    }
    for (const m of expanded) {
      this.handleHubEvent({ name: m.name, payload: m.env });
    }
  }

  /** Resolve an embedded-message shape to {eventName, envelope}. */
  private routeEmbedded(item: unknown): { name: string; env: Envelope } | null {
    if (!item || typeof item !== 'object') return null;
    const obj = item as Record<string, unknown>;
    const known = new Set([
      'PlayerJoined',
      'StartGame',
      'StartBet',
      'EndBet',
      'StartPlay',
      'Result',
      'PlaceBet',
    ]);

    // Embedded Name/name field.
    const embedded = obj.Name ?? obj.name;
    if (typeof embedded === 'string' && known.has(embedded)) {
      return { name: embedded, env: this.normalizeEnvelope(obj) };
    }
    // Single-key wrapper { StartBet: envelope }.
    const keys = Object.keys(obj);
    if (keys.length === 1 && known.has(keys[0])) {
      return { name: keys[0], env: this.normalizeEnvelope(obj[keys[0]]) };
    }
    return null;
  }

  private runMultiplayerResultAnimation(centerCard: number, winner: GameResult): void {
    this.cancelTimers();
    this.d.rive.setCenterCard(centerCard);
    this.scheduleTimer(() => this.d.rive.setCardAnim(3), 1000);
    this.scheduleTimer(() => this.d.rive.setCardAnim(4), 2000);
    this.scheduleTimer(() => {
      this.d.rive.setCardAnim(0);
      this.d.rive.setBallShoot(ballShootFor(winner));
    }, 3000);
  }

  // -----------------------------------------------------------------
  // Multiplayer countdowns
  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
    this.d.controls.setVisible(true);
  }

  // -----------------------------------------------------------------
  // Envelope normalization (football: Flag-based, last-envelope-wins)
  // -----------------------------------------------------------------

  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      // Iterate reversed; last envelope-shaped element wins.
      for (let i = payload.length - 1; i >= 0; i--) {
        const item = payload[i];
        if (looksLikeEnvelope(item)) return item as Record<string, unknown>;
      }
      // Fall back to the last object element.
      for (let i = payload.length - 1; i >= 0; i--) {
        const item = payload[i];
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  // -----------------------------------------------------------------

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// =====================================================================
// Helpers
// =====================================================================

function money2(n: number): string {
  return n.toFixed(2);
}
function int0(n: number): string {
  return String(Math.round(n));
}
function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function looksLikeEnvelope(o: unknown): boolean {
  if (!o || typeof o !== 'object' || Array.isArray(o)) return false;
  const m = o as Record<string, unknown>;
  return (
    'Flag' in m || 'Index' in m || 'Data' in m || 'Content' in m || 'State' in m
  );
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

const GOAL_LABEL: Record<GameResult, string> = {
  left: 'Left Goal',
  center: 'Center Goal',
  right: 'Right Goal',
};

function formatGameResult(winner: GameResult): string {
  return `${GOAL_LABEL[winner]} wins`;
}

function formatBetDetails(bets: {
  bets: readonly BetType[];
  amountFor(b: BetType): number;
}): string {
  const parts: string[] = [];
  for (const b of bets.bets) {
    const amt = bets.amountFor(b);
    if (amt > 0) parts.push(`${GOAL_LABEL[b]}:${formatCurrency(amt)}`);
  }
  return parts.join(', ');
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

/** Probe `Username` → `username` → `UserName` → `userName`. */
function pickUserName(item: Record<string, unknown>): string {
  const raw = item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}

/**
 * Current user's balance from a Result/PlaceBet envelope's `Data`.
 * Adapts `_extractBalanceForUser` (game_bloc.dart:1665-1695):
 *   1. probe Username → username → UserName → userName
 *   2. SKIP entries with empty/mismatched username
 *   3. matched entry with null Balance → return null (don't update)
 *
 * NOTE: username matching is CASE-INSENSITIVE here (both sides lowercased),
 * an intentional divergence from Flutter's case-sensitive compare — it matches
 * the Dragon Tiger reference port and is more robust to servers that echo the
 * username with different casing than the client submitted.
 */
function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;
  const items = Array.isArray(data) ? data : [data];
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const u = pickUserName(item);
    if (!u || u !== userNameLower) continue;
    const b = item.Balance ?? item.balance;
    if (b == null) return null;
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Current user's WinLose from a Result envelope's `Data`. WinLose username
 * probe is only UserName/username (game_bloc.dart:1327-1343, asymmetric vs
 * balance). Accumulates across matching rows. Field probe order:
 * WinLost → winLost → WinLose → winLose.
 */
function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;
  const items = Array.isArray(data) ? data : [data];
  let sum = 0;
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const name = String(item.UserName ?? item.username ?? '').toLowerCase();
    if (!name || name !== userNameLower) continue;
    sum += asNumber(item.WinLost ?? item.winLost ?? item.WinLose ?? item.winLose);
  }
  return sum;
}
